// Пар у манометров: струя из крана машины и завеса под самими манометрами.
//
// Струя бьёт вверх-влево из левого крана, мимо котла. Пускают её два события: открылась
// створка мини-слота на nudge respin и начался бонус, см. features/steamSpin/steamSpin.ts.
// Живёт она одну раскадровку и тает сама, поэтому уводить её нечем.
//
// Завеса стоит всё время бонуса: пар медленно ползёт под манометрами и уходит вместе с бонусом.
// Полос у неё три, по одной на манометр, и каждая начинает раскадровку со своего места. Начни
// они разом, пар шёл бы под манометрами одной волной, и рука художника читалась бы сразу.
//
// Пар шипит, а не гудит. Гудок паровоза — отклик на выигрыш и другого повода звучать не имеет,
// см. ADR-49, а створку мини-слота открывает не выигрыш. Шипение идёт ровно столько же, сколько
// струя: кусок звука вырезан по её длине, см. SFX_SPRITE.
//
// Координаты здесь те же, что у барабанов: это пиксели картинки барабанов.

import { Container } from 'pixi.js';

import { GAUGE_STEAM_SKELETON, REELS_GRID, STEAM_JET_SKELETON, STEAM_VALVE } from '../assets/catalog';
import type { AssetManager, Spine, TrackEntry } from '../assets/manager';
import { sfx } from '../sound/sound';

import { animationSeconds, spineOrigin } from './scene';

/** Длина струи в координатах картинки барабанов. Взята по машине: пар уходит вровень с котлом. */
const JET_LENGTH: number = 260;

/** Куда бьёт струя, в градусах от вертикали. Влево от машины, мимо её рамки. */
const JET_ANGLE: number = -35;

/**
 * Высота полосы завесы там же.
 *
 * Полоса между циферблатами и закрытым полем узкая, и вся завеса в неё не встаёт: низом она
 * уходит за створки поля. Так и надо — пар выбивается из машины, а не висит поверх неё.
 */
const VEIL_HEIGHT: number = 180;

/**
 * Насколько середина завесы стоит ниже оси манометра.
 *
 * Завеса начинается там, где кончается циферблат: пар идёт под манометрами, а не по ним.
 * Циферблаты нарисованы на самой картинке машины, и лечь под них узлом пар не может —
 * его место задаётся высотой, а не порядком отрисовки.
 */
const VEIL_DROP: number = REELS_GRID.gauge.size / 2 + VEIL_HEIGHT / 2;

/** Насколько густ пар под манометрами. В полную силу он закрыл бы циферблаты. */
const VEIL_ALPHA: number = 0.8;

/** Сколько завеса набирает густоту и сколько теряет. Пар ползёт медленно, и уходит он так же. */
const VEIL_FADE_SECONDS: number = 0.6;

/** Пар у манометров: струя из крана и завеса под ними. */
export class Steam {
  readonly view: Container = new Container();

  /** Узел струи. Он и повёрнут туда, куда струя бьёт: сама она нарисована стоймя. */
  readonly #jetView: Container = new Container();

  readonly #veilView: Container = new Container();

  readonly #jet: Spine;

  /** Сколько идёт раскадровка струи. По ней струя и снимается с экрана. */
  readonly #jetSeconds: number;

  /** Сколько струе осталось бить. Ноль — струи на экране нет. */
  #jetLeft = 0;

  /** Стоит ли завеса. Её ставит бонус, и его же конец её снимает. */
  #holding = false;

  constructor(assets: AssetManager) {
    this.#jet = assets.spine(STEAM_JET_SKELETON);

    const scale: number = JET_LENGTH / this.#jet.skeleton.data.height;

    // Начало координат струи стоит не в её основании, а на трети высоты, см. STEAM_JET_SKELETON.
    // Поэтому струя ставится своим низом на начало узла: габарит целиком уходит вверх от него.
    const origin = spineOrigin(this.#jet.skeleton.data, scale, 0, -JET_LENGTH / 2);

    this.#jet.scale.set(scale);
    this.#jet.position.set(origin.x, origin.y);

    this.#jetSeconds = animationSeconds(this.#jet, STEAM_JET_SKELETON.animations.jet);

    this.#jetView.rotation = (JET_ANGLE * Math.PI) / 180;
    this.#jetView.position.set(STEAM_VALVE.x, STEAM_VALVE.y);
    this.#jetView.visible = false;
    this.#jetView.addChild(this.#jet);

    const veils: Spine[] = REELS_GRID.gauge.pivots.map((pivot: { readonly x: number; readonly y: number }): Spine => {
      const veil: Spine = assets.spine(GAUGE_STEAM_SKELETON);

      // Полоса нарисована вокруг начала координат, поэтому оно и есть её середина.
      veil.scale.set(VEIL_HEIGHT / veil.skeleton.data.height);
      veil.position.set(pivot.x, pivot.y + VEIL_DROP);

      this.#veilView.addChild(veil);

      return veil;
    });

    // Каждая полоса начинает круг со своего места: разом они шли бы одной волной.
    const round: number = animationSeconds(veils[0], GAUGE_STEAM_SKELETON.animations.roll);

    for (let index = 0; index < veils.length; index++) {
      const track: TrackEntry = veils[index].state.setAnimation(0, GAUGE_STEAM_SKELETON.animations.roll, true);
      track.trackTime = (round * index) / veils.length;
    }

    this.#veilView.visible = false;
    this.#veilView.alpha = 0;

    this.view.addChild(this.#veilView, this.#jetView);
  }

  /**
   * Пускает струю из крана вместе с её шипением.
   *
   * Идущая струя начинается заново: створки мини-слота открываются по одной, и пар бьёт
   * на каждой, а не тянется одним облаком через все три.
   */
  burst(): void {
    this.#jetLeft = this.#jetSeconds;
    this.#jetView.visible = true;
    this.#jet.state.setAnimation(0, STEAM_JET_SKELETON.animations.jet, false);

    sfx('steamJet');
  }

  /** Ставит завесу под манометры. Держится она, пока её не снимут. */
  hold(): void {
    this.#holding = true;
    this.#veilView.visible = true;
  }

  /** Снимает завесу. Пар редеет своё время, а не пропадает у игрока на глазах, см. ADR-50. */
  release(): void {
    this.#holding = false;
  }

  update(seconds: number): void {
    this.#advanceJet(seconds);
    this.#advanceVeil(seconds);
  }

  /** Ведёт срок струи. Отбив своё, она уходит с экрана. */
  #advanceJet(seconds: number): void {
    if (this.#jetLeft <= 0)
      return;

    this.#jetLeft -= seconds;

    if (this.#jetLeft <= 0)
      this.#jetView.visible = false;
  }

  /** Ведёт густоту завесы: она набирает её на входе и теряет на выходе. */
  #advanceVeil(seconds: number): void {
    if (!this.#veilView.visible)
      return;

    const step: number = (VEIL_ALPHA * seconds) / VEIL_FADE_SECONDS;
    const veil: Container = this.#veilView;

    veil.alpha = this.#holding ? Math.min(veil.alpha + step, VEIL_ALPHA) : Math.max(veil.alpha - step, 0);

    if (veil.alpha <= 0)
      veil.visible = false;
  }
}
